import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	ACTION,
	ACTION_SUBJECT,
} from '@atlaskit/editor-common/analytics/types/enums';
import HoverLinkOverlay from '@atlaskit/editor-common/HoverLinkOverlay';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { NamedPluginStatesFromInjectionAPI } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { extractSmartLinkEmbed } from '@atlaskit/link-extractors/extract-smart-link-embed';
import { isWithinPreviewPanelIFrame } from '@atlaskit/linking-common/utils/is-within-preview-panel-iframe';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { getObjectAri, getObjectName, getObjectIconUrl } from '@atlaskit/smart-card';
import { useSmartLinkDestinationUrl } from '@atlaskit/smart-card/hook/use-smart-link-destination-url';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { cardPlugin } from '../cardPlugin';
import { registerRemoveOverlay } from '../pm-plugins/actions';
import { pluginKey } from '../pm-plugins/plugin-key';
import { AwarenessWrapper } from '../ui/AwarenessWrapper';
import { PreviewInvoker } from '../ui/preview/PreviewInvoker';
import type { SmartCardProps } from './genericCard';
import { InlineCard } from './inlineCardBase';

export type InlineCardWithAwarenessProps = {
	isOverlayEnabled?: boolean;
	isPulseEnabled?: boolean;
	isSelected?: boolean;
};

// Module scope so the stored callback's closure chain cannot reach the
// component scope (getPos, node, view) via V8 context sharing.
const createRemoveOverlay =
	(setIsInsertedRef: React.MutableRefObject<((value: boolean) => void) | undefined>) => () =>
		setIsInsertedRef.current?.(false);

const selector = (
	states: NamedPluginStatesFromInjectionAPI<
		ExtractInjectionAPI<typeof cardPlugin>,
		'selection' | 'editorViewMode'
	>,
) => {
	return {
		mode: states.editorViewModeState?.mode,
		selection: states.selectionState?.selection,
	};
};

export const InlineCardWithAwareness: React.MemoExoticComponent<
	({
		node,
		cardContext,
		actionOptions,
		useAlternativePreloader,
		view,
		getPos,
		pluginInjectionApi,
		onClick,
		isPulseEnabled,
		isOverlayEnabled,
		isSelected,
		isPageSSRed,
		provider,
		appearance,
	}: SmartCardProps & InlineCardWithAwarenessProps) => React.JSX.Element
> = memo(
	({
		node,
		cardContext,
		actionOptions,
		useAlternativePreloader,
		view,
		getPos,
		pluginInjectionApi,
		onClick,
		isPulseEnabled,
		isOverlayEnabled,
		isSelected,
		isPageSSRed,
		provider,
		appearance,
	}: SmartCardProps & InlineCardWithAwarenessProps): React.JSX.Element => {
		const [isHovered, setIsHovered] = useState(false);
		const [isInserted, setIsInserted] = useState(false);
		const setIsInsertedRef = useRef<((value: boolean) => void) | undefined>(setIsInserted);
		useEffect(() => {
			return () => {
				if (isExperimentEnabled('platform_editor_reduce_memory_leaks')) {
					setIsInsertedRef.current = undefined;
				}
			};
		}, []);
		const [isResolvedViewRendered, setIsResolvedViewRendered] = useState(false);
		const editorAppearance = pluginInjectionApi?.card.sharedState.currentState()?.editorAppearance;
		const destinationUrl = useSmartLinkDestinationUrl(node.attrs.url);

		// Remove tr: Transaction | undefined parameter with platform_editor_reduce_forced_layout_2 cleanup.
		const onResolve = useCallback((tr: Transaction | undefined, title?: string): void => {
			if (tr) {
				const metadata = tr.getMeta(pluginKey);
				const removeOverlay = isExperimentEnabled('platform_editor_reduce_memory_leaks')
					? createRemoveOverlay(setIsInsertedRef)
					: () => setIsInserted(false);
				if (metadata && metadata.type === 'REGISTER') {
					registerRemoveOverlay(removeOverlay, metadata.info)(tr);
				} else {
					registerRemoveOverlay(removeOverlay)(tr);
				}
			}

			if (title) {
				setIsResolvedViewRendered(true);
			}
		}, []);

		const markMostRecentlyInsertedLink = useCallback(
			(isLinkMostRecentlyInserted: boolean) => {
				if (isOverlayEnabled && !isExperimentEnabled('platform_editor_reduce_forced_layout_2')) {
					setIsInserted(isLinkMostRecentlyInserted);
				}
			},
			[
				isExperimentEnabled('platform_editor_reduce_forced_layout_2')
					? undefined
					: isOverlayEnabled,
			],
		);

		const setOverlayHoveredStyles = useCallback(
			(isHovered: boolean) => {
				if (isOverlayEnabled) {
					setIsHovered(isHovered);
				}
			},
			[isOverlayEnabled],
		);

		const { mode, selection } = useSharedPluginStateWithSelector(
			pluginInjectionApi,
			['selection', 'editorViewMode'],
			selector,
		);
		const floatingToolbarNode = selection instanceof NodeSelection && selection.node;
		// This is a prop to show Hover card, Hover card should be shown only in Live View and Classic Renderer (note when only Editor controls enabled we don't show in Live view)
		const showHoverPreview = floatingToolbarNode !== node;

		const innerCardWithOpenButtonOverlay = useMemo(
			() => (
				<HoverLinkOverlay
					isVisible={isResolvedViewRendered}
					url={node.attrs.url}
					destinationUrl={destinationUrl}
					compactPadding={editorAppearance === 'comment' || editorAppearance === 'chromeless'}
					editorAnalyticsApi={pluginInjectionApi?.analytics?.actions}
					view={view}
				>
					<InlineCard
						node={node}
						view={view}
						getPos={getPos}
						useAlternativePreloader={useAlternativePreloader}
						actionOptions={actionOptions}
						onResolve={onResolve}
						onClick={onClick}
						cardContext={cardContext}
						isHovered={isHovered}
						isPageSSRed={isPageSSRed}
						provider={provider}
						pluginInjectionApi={pluginInjectionApi}
						disablePreviewPanel={true}
					/>
				</HoverLinkOverlay>
			),
			[
				isResolvedViewRendered,
				node,
				editorAppearance,
				view,
				getPos,
				useAlternativePreloader,
				actionOptions,
				onResolve,
				onClick,
				cardContext,
				isHovered,
				isPageSSRed,
				provider,
				pluginInjectionApi,
				destinationUrl,
			],
		);

		const innerCardOriginal = useMemo(
			() => (
				<InlineCard
					node={node}
					view={view}
					getPos={getPos}
					useAlternativePreloader={useAlternativePreloader}
					actionOptions={actionOptions}
					onResolve={onResolve}
					onClick={onClick}
					cardContext={cardContext}
					isHovered={isHovered}
					isPageSSRed={isPageSSRed}
					provider={provider}
					pluginInjectionApi={pluginInjectionApi}
					showHoverPreview={false}
				/>
			),
			[
				actionOptions,
				cardContext,
				getPos,
				isHovered,
				node,
				onClick,
				onResolve,
				useAlternativePreloader,
				view,
				isPageSSRed,
				provider,
				pluginInjectionApi,
			],
		);

		const shouldShowOpenButtonOverlay = useMemo(() => {
			const shouldShowOpenButtonOverlayInChomeless = editorAppearance === 'chromeless';

			return (
				mode === 'edit' || editorAppearance === 'comment' || shouldShowOpenButtonOverlayInChomeless
			);
		}, [mode, editorAppearance]);

		let innerCard = shouldShowOpenButtonOverlay
			? innerCardWithOpenButtonOverlay
			: innerCardOriginal;

		if (mode === 'view') {
			const url = node.attrs.url;
			const cardState = cardContext?.value?.store?.getState()[url];
			if (cardState) {
				const ari = getObjectAri(cardState.details);
				const name = getObjectName(cardState.details);
				const iconUrl = getObjectIconUrl(cardState.details);
				const isPanelAvailable = ari && cardContext?.value?.isPreviewPanelAvailable?.({ ari });
				const openPreviewPanel = cardContext?.value?.openPreviewPanel;
				const isPreviewPanelAvailable = Boolean(openPreviewPanel && isPanelAvailable);

				const firePreviewPanelClickEvent = ({
					previewType,
				}: {
					previewType: 'panel' | 'modal';
				}) => {
					const store = cardContext?.value?.store;
					const urlState = store?.getState()[url || ''];
					if (pluginInjectionApi?.analytics?.actions) {
						pluginInjectionApi?.analytics?.actions?.fireAnalyticsEvent({
							action: ACTION.CLICKED,
							actionSubject: ACTION_SUBJECT.SMART_LINK,
							actionSubjectId: ACTION_SUBJECT_ID.HOVER_LABEL,
							eventType: EVENT_TYPE.UI,
							attributes: {
								previewType,
								destinationProduct: urlState?.details?.meta?.product ?? null,
								destinationSubproduct: urlState?.details?.meta?.subproduct ?? null,
							},
						});
					}
				};

				const innerCardWithPanelButtonOverlay = (
					<PreviewInvoker url={url} appearance="inline">
						{({ canPreview, invokePreview }) => {
							const isPreviewModalAvailable = Boolean(canPreview && invokePreview);
							// In view mode we show HoverLinkOverlay only with if preview mode or panel is available
							// otherwise a use can click on smartlink itself to open the link in a new tab.
							const isPreviewAvailable = isPreviewPanelAvailable || isPreviewModalAvailable;
							// When inside preview panel iframe, hide the overlay button
							const isInPreviewPanel = isWithinPreviewPanelIFrame();
							const showPanelButton = isInPreviewPanel
								? isPreviewPanelAvailable
								: isPreviewAvailable;

							if (isPreviewAvailable) {
								return (
									<HoverLinkOverlay
										isVisible={isResolvedViewRendered}
										url={url}
										destinationUrl={destinationUrl}
										compactPadding={
											editorAppearance === 'comment' || editorAppearance === 'chromeless'
										}
										editorAnalyticsApi={pluginInjectionApi?.analytics?.actions}
										view={view}
										showPanelButton={showPanelButton}
										showPanelButtonIcon={
											isPreviewAvailable && isPreviewPanelAvailable ? 'panel' : 'modal'
										}
										// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
										onClick={(event) => {
											if (isPreviewPanelAvailable) {
												event.preventDefault();
												openPreviewPanel?.({
													url,
													ari: ari || '',
													name: name || '',
													iconUrl,
													panelData: {
														embedUrl: expValEquals(
															'platform_hover_card_preview_panel',
															'cohort',
															'test',
														)
															? extractSmartLinkEmbed(cardState?.details)?.src
															: undefined,
													},
												});
												firePreviewPanelClickEvent({ previewType: 'panel' });
											} else if (isPreviewModalAvailable) {
												event.preventDefault();
												invokePreview?.();
												firePreviewPanelClickEvent({ previewType: 'modal' });
											}
										}}
									>
										<InlineCard
											node={node}
											view={view}
											getPos={getPos}
											useAlternativePreloader={useAlternativePreloader}
											actionOptions={actionOptions}
											onResolve={onResolve}
											onClick={onClick}
											cardContext={cardContext}
											isHovered={isHovered}
											isPageSSRed={isPageSSRed}
											provider={provider}
											pluginInjectionApi={pluginInjectionApi}
											showHoverPreview={mode === 'view' && showHoverPreview}
											disablePreviewPanel={true}
										/>
									</HoverLinkOverlay>
								);
							} else {
								return (
									<InlineCard
										node={node}
										view={view}
										getPos={getPos}
										useAlternativePreloader={useAlternativePreloader}
										actionOptions={actionOptions}
										onResolve={onResolve}
										onClick={onClick}
										cardContext={cardContext}
										isHovered={isHovered}
										isPageSSRed={isPageSSRed}
										provider={provider}
										pluginInjectionApi={pluginInjectionApi}
										showHoverPreview={mode === 'view' && showHoverPreview}
									/>
								);
							}
						}}
					</PreviewInvoker>
				);
				innerCard = innerCardWithPanelButtonOverlay;
			}
		}

		const getPosFunction = typeof getPos === 'function' ? getPos : undefined;
		const placeholderUniqId = getPosFunction?.() || 0;

		return isOverlayEnabled || isPulseEnabled ? (
			<AwarenessWrapper
				isOverlayEnabled={isOverlayEnabled}
				isPulseEnabled={isPulseEnabled}
				cardContext={cardContext}
				getPos={getPos}
				isHovered={isHovered}
				isInserted={isInserted}
				url={node.attrs.url}
				isSelected={isSelected}
				isResolvedViewRendered={isResolvedViewRendered}
				markMostRecentlyInsertedLink={markMostRecentlyInsertedLink}
				pluginInjectionApi={pluginInjectionApi}
				setOverlayHoveredStyles={setOverlayHoveredStyles}
				appearance={appearance}
			>
				{innerCard}
			</AwarenessWrapper>
		) : (
			<span
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="card"
				data-vc="editor-plugin-inline-card"
				data-ssr-placeholder={`editor-plugin-inline-card-${placeholderUniqId}`}
				data-ssr-placeholder-replace={`editor-plugin-inline-card-${placeholderUniqId}`}
			>
				{innerCard}
			</span>
		);
	},
);
